import { useLazyComponent } from '@trello/use-lazy-component';

import type { FeedbackCollector } from './FeedbackCollector';

type UseLazyFeedbackCollectorProps = {
  preload: boolean;
};

/**
 * Returns a lazily-loaded version of the {@link FeedbackCollector} component.
 *
 * Uses {@link useLazyComponent} to code-split the `FeedbackCollector` into a
 * separate webpack chunk (`"feedback-collector"`), deferring its download until
 * the component is first rendered — or earlier when `preload` is `true`.
 *
 * @param params - The hook configuration object.
 * @param params.preload - When `true`, begins loading the `FeedbackCollector`
 *   chunk immediately rather than waiting for the component to mount.
 * @returns A `React.LazyExoticComponent` wrapping the `FeedbackCollector`,
 *   suitable for rendering inside a `React.Suspense` boundary.
 */
export const useLazyFeedbackCollector = ({
  preload,
}: UseLazyFeedbackCollectorProps): React.LazyExoticComponent<
  typeof FeedbackCollector
> =>
  useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "feedback-collector" */ './FeedbackCollector'
      ),
    { namedImport: 'FeedbackCollector', preload },
  );
